(() => {
  'use strict';

  if (window.__mascalmaHomePageLoaded) {
    if (typeof window.__mascalmaInitHomePages === 'function') {
      window.__mascalmaInitHomePages(document);
    }
    return;
  }

  window.__mascalmaHomePageLoaded = true;

  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  class MascalmaHomePage {
    constructor(root) {
      this.root = root;
      this.primaryButton = root.querySelector('[data-home-primary]');
      this.sticky = root.querySelector('[data-home-sticky]');
    }

    init() {
      this.setupVideos();
      this.setupCarousels();
      this.setupSticky();
    }

    setupVideos() {
      this.root.querySelectorAll('video').forEach((video) => {
        video.controls = false;
        video.muted = true;
        video.loop = true;
        video.playsInline = true;

        if (reducedMotion()) {
          video.autoplay = false;
          video.pause();
          return;
        }

        video.autoplay = true;
        const playPromise = video.play();
        if (playPromise?.catch) playPromise.catch(() => {});
      });
    }

    setupCarousels() {
      this.root.querySelectorAll('[data-home-carousel]').forEach((carousel) => {
        const track = carousel.querySelector('[data-home-carousel-track]');
        const slides = Array.from(carousel.querySelectorAll('[data-home-slide]'));
        const previous = carousel.querySelector('[data-home-prev]');
        const next = carousel.querySelector('[data-home-next]');
        const dots = carousel.querySelector('[data-home-dots]');

        if (!track || slides.length === 0) return;

        let activeIndex = 0;
        let ticking = false;
        const behavior = reducedMotion() ? 'auto' : 'smooth';

        const targetLeft = (slide) => {
          if (carousel.dataset.homeCarousel === 'reviews') {
            return slide.offsetLeft - ((track.clientWidth - slide.offsetWidth) / 2);
          }
          return slide.offsetLeft;
        };

        const goTo = (index, scrollBehavior = behavior) => {
          const target = Math.max(0, Math.min(index, slides.length - 1));

          track.scrollTo({
            left: Math.max(0, targetLeft(slides[target])),
            behavior: scrollBehavior
          });
        };

        const update = () => {
          const center = track.scrollLeft + (track.clientWidth / 2);
          let nearest = Number.POSITIVE_INFINITY;

          slides.forEach((slide, index) => {
            const distance = Math.abs(
              center - (slide.offsetLeft + (slide.offsetWidth / 2))
            );

            if (distance < nearest) {
              nearest = distance;
              activeIndex = index;
            }
          });

          dots?.querySelectorAll('.mh-carousel__dot').forEach((dot, index) => {
            const active = index === activeIndex;

            dot.classList.toggle('is-active', active);
            dot.setAttribute('aria-current', active ? 'true' : 'false');
          });

          if (previous) previous.disabled = track.scrollLeft <= 2;

          if (next) {
            next.disabled = (
              track.scrollLeft + track.clientWidth
              >= track.scrollWidth - 2
            );
          }

          ticking = false;
        };

        if (dots) {
          dots.innerHTML = '';

          slides.forEach((slide, index) => {
            const dot = document.createElement('button');

            dot.type = 'button';
            dot.className = 'mh-carousel__dot';
            dot.setAttribute(
              'aria-label',
              `Ir al elemento ${index + 1}`
            );
            dot.addEventListener('click', () => goTo(index));

            dots.appendChild(dot);
          });
        }

        previous?.addEventListener(
          'click',
          () => goTo(activeIndex - 1)
        );

        next?.addEventListener(
          'click',
          () => goTo(activeIndex + 1)
        );

        track.addEventListener('scroll', () => {
          if (ticking) return;

          ticking = true;
          window.requestAnimationFrame(update);
        }, { passive: true });

        if (slides.length <= 1) {
          if (previous) previous.hidden = true;
          if (next) next.hidden = true;
          if (dots) dots.hidden = true;
        }

        window.requestAnimationFrame(() => {
          if (
            carousel.dataset.startCentered === 'true'
            && window.innerWidth < 750
            && slides.length >= 3
          ) {
            goTo(1, 'auto');
          }

          update();
        });
      });
    }

    setupSticky() {
      if (!this.sticky || !this.primaryButton) return;

      const footerTargets = Array.from(
        new Set(
          document.querySelectorAll(
            '.shopify-section-group-footer-group, footer.footer, footer[role="contentinfo"]'
          )
        )
      );

      const update = () => {
        const buttonRect = this.primaryButton.getBoundingClientRect();

        const footerVisible = footerTargets.some((footer) => {
          const footerRect = footer.getBoundingClientRect();

          return (
            footerRect.top <= window.innerHeight + 12
            && footerRect.bottom >= 0
          );
        });

        const visible = (
          window.innerWidth < 750
          && buttonRect.bottom < 0
          && !footerVisible
        );

        this.sticky.hidden = !visible;
        this.root.classList.toggle('has-home-sticky', visible);
      };

      window.addEventListener(
        'scroll',
        update,
        { passive: true }
      );

      window.addEventListener(
        'resize',
        update,
        { passive: true }
      );

      update();
    }
  }

  const initHomePages = (scope = document) => {
    const roots = [];

    if (scope.matches?.('[data-mascalma-home]')) {
      roots.push(scope);
    }

    scope.querySelectorAll?.(
      '[data-mascalma-home]'
    ).forEach((root) => {
      roots.push(root);
    });

    roots.forEach((root) => {
      if (root.dataset.mascalmaHomeInitialized === 'true') return;

      root.dataset.mascalmaHomeInitialized = 'true';

      const page = new MascalmaHomePage(root);

      root.mascalmaHomePage = page;
      page.init();
    });
  };

  window.__mascalmaInitHomePages = initHomePages;

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => initHomePages(document),
      { once: true }
    );
  } else {
    initHomePages(document);
  }

  document.addEventListener(
    'shopify:section:load',
    (event) => initHomePages(event.target)
  );
})();